import { useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { format, addMonths } from "date-fns";

import {
  useCreatePurchaseDetailsMutation,
  useCreateWorldBoxOrderMutation,
  useCreateCompanyFollowMutation,
} from "../../../../store/Company/companyApiSlice";
import { CommonToast } from "../../../common/Toast";
import { profileStatus, LISTING, WARNING } from "./profileStatus";
import { customerReference, worldboxPostData } from "./useGetTheBusiness";

/* Spending a credit — the credit notice's "Process". */
export const purchasePayload = ({
  userInfo,
  companyId,
  type,
  selectedEnterprise,
  reference,
}) => ({
  planID: userInfo?.individual?.[0]?.individualSubscription?.[0]?.subscriptionID,
  purchaseDate: format(new Date(), "yyyy-MM-dd"),
  expireDate: format(addMonths(new Date(), 1), "yyyy-MM-dd"),
  userID: userInfo?.individual?.[0]?.userID,
  companyID: companyId,
  purchaseType: "Subscription",
  ...(type === "enterprise" && {
    enterpriseID: selectedEnterprise?.id,
    customerreference: reference,
  }),
});

/* Transcribed: a listing or a draft gets the WorldBox order; anything else
   gets followed instead. */
export const needsWorldBoxOrder = (company, type) => {
  const status = profileStatus(company);
  return type === "credit" && (status === LISTING || status === WARNING);
};

export const shouldFollowAfterPurchase = (company, isFollowing) => {
  const status = profileStatus(company);
  if (status === LISTING || status === WARNING) return false;
  return !isFollowing;
};

export const useSpendCredit = ({ company, trevexId, formData, onDone } = {}) => {
  const { id } = useParams();
  const { userInfo, token } = useSelector((state) => state.auth);

  const [createPurchaseDetails] = useCreatePurchaseDetailsMutation();
  const [createOrderWorldBox] = useCreateWorldBoxOrderMutation();
  const [createFollow] = useCreateCompanyFollowMutation();
  const [busy, setBusy] = useState(false);

  const userID = userInfo?.individual?.[0]?.userID;

  /* `type` is "credit" or "enterprise" — the credit notice passes it, and it
     decides both the purchase payload and whether a WorldBox order follows. */
  const spend = async (type = "credit", { selectedEnterprise, isFollowing } = {}) => {
    if (busy) return false;
    setBusy(true);
    const reference = customerReference(formData);

    try {
      const res = await createPurchaseDetails({
        data: purchasePayload({
          userInfo,
          companyId: id,
          type,
          selectedEnterprise,
          reference,
        }),
        token,
      });

      if (res?.error) {
        CommonToast.error(res.error?.data?.error ?? "Purchase failure");
        return false;
      }
      if (!res?.data) return false;

      if (shouldFollowAfterPurchase(company, isFollowing) && trevexId) {
        try {
          await createFollow({
            data: { companyID: company?.companyID, userID },
            token,
          }).unwrap();
        } catch {
          /* The live code does not guard this, so a failed follow took the whole handler down after the purchase had already been written. */
        }
      }

      if (needsWorldBoxOrder(company, type)) {
        const postData = worldboxPostData({ company, id, userID, formData });
        try {
          await createOrderWorldBox({
            data: res?.data?.purchaseID
              ? { ...postData, purchaseID: res.data.purchaseID }
              : postData,
            token,
          }).unwrap();
        } catch {
          /* Transcribed: swallowed. See the note at the top. */
        }
      }

      /* Transcribed: the parked company id is released and the flow ends. */
      localStorage.removeItem("payasyougoCompanyID");
      onDone?.();
      return true;
    } finally {
      setBusy(false);
    }
  };

  return { spend, busy };
};

export default useSpendCredit;
